'use client';
import { useMediaQuery } from '@react-hookz/web/esm/useMediaQuery';
import { size } from 'lodash';
import { useCallback, useRef } from 'react';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import Image from 'molecules/Image';
import 'swiper/css';
import 'components/Trustbar/styles/Trustbar.css';

import { containerStyles, imageStyles } from 'components/Trustbar/styles/TrustbarCarousel.styles';

import screens from 'theme/spacing/screens';

import type { TrustbarVariantProps } from '..';
import type { StaticImage } from 'molecules/Image';
import type { FC } from 'react';
import type { SwiperRef } from 'swiper/react';

const TrustbarCarousel: FC<TrustbarVariantProps> = ({ logos }) => {
  const controlsRef = useRef<SwiperRef | null>(null);
  const allLogos = Array.from({ length: 3 }).flatMap(() => logos);

  const isMobile = useMediaQuery(`(max-width: ${screens.sm})`) || false;
  const isTablet = useMediaQuery(`(max-width: ${screens.md})`) || false;
  const isDesktop = useMediaQuery(`(min-width: ${screens.xl})`) || true;

  const generateSlidePreview = useCallback(
    (points: number) => {
      if (points === 1440) {
        if (size(allLogos) < 7) {
          return size(allLogos);
        } else {
          return 7;
        }
      } else if (points === 992) {
        if (size(allLogos) < 5) {
          return size(allLogos);
        } else {
          return 5;
        }
      }

      if (size(allLogos) > 3) {
        return 3;
      } else {
        return size(allLogos);
      }
    },
    [isDesktop, isTablet, isMobile],
  );

  const handleMouseEnter = () => {
    controlsRef?.current?.swiper?.autoplay.stop();
  };

  const handleMouseLeave = () => {
    controlsRef?.current?.swiper?.autoplay.start();
  };

  return (
    <div
      className={twMerge(containerStyles())}
      onMouseEnter={() => handleMouseEnter()}
      onMouseLeave={() => handleMouseLeave()}
    >
      <Swiper
        ref={controlsRef}
        className="trustbar-slider"
        loop={true}
        modules={[Autoplay]}
        slidesPerView={3}
        spaceBetween={32}
        autoplay={{
          delay: 0,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }}
        breakpoints={{
          568: {
            slidesPerView: generateSlidePreview(576),
            spaceBetween: 32,
          },
          992: {
            slidesPerView: generateSlidePreview(992),
            spaceBetween: 32,
          },
          1440: {
            slidesPerView: generateSlidePreview(1440),
            spaceBetween: 32,
          },
        }}
        speed={3000}
      >
        {allLogos?.map(logo => (
          <SwiperSlide key={uuidv4()}>
            <Image image={logo as StaticImage} alt={logo?.title || 'Logo'} className={twMerge(imageStyles())} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default TrustbarCarousel;
